import { autoUpdate, computePosition, flip, offset, shift, size } from "@floating-ui/dom"

export const supportsAnchorPositioning = CSS.supports("position-area: block-end")

let anchorSequence = 0

function anchorNames(element) {
  return element.style.anchorName.split(",").map(name => name.trim()).filter(Boolean)
}

// Safari 26.0–26.3 shipped CSS Anchor Positioning bugs that can place native
// popovers off-screen. Force only those releases onto Floating UI positioning;
// remove this guard when they leave our supported-browser window.
export function requiresSafariAnchorPositioningFallback(userAgent) {
  const match = userAgent.match(/\bVersion\/(\d+)\.(\d+)(?:\.\d+)?\b.*\bSafari\//)
  if (!match) return false

  return Number(match[1]) === 26 && Number(match[2]) < 4
}

/**
 * Resolve the positioning engine for a popover.
 *
 * @param {string} [explicitEngine] - "anchor" or "floating" when forced.
 *   Defaults to the window.popoverEngine global config — tests/previews set it
 *   to force "floating", which Chromium's anchor support would otherwise make
 *   unreachable.
 * @param {string} [userAgent]
 * @returns {"anchor" | "floating"}
 */
export function resolvePopoverEngine(explicitEngine = window.popoverEngine, userAgent = navigator.userAgent) {
  if (explicitEngine) return explicitEngine

  if (requiresSafariAnchorPositioningFallback(userAgent)) {
    return "floating"
  }

  if (supportsAnchorPositioning) {
    return "anchor"
  }

  return "floating"
}

/**
 * CSS anchor + Floating UI positioning for native [popover] surfaces
 * (dropdown menus, selects, …). Construct on connect: the engine is resolved
 * here (honouring the window.popoverEngine global config), stamped on the
 * floating element as data-popover-engine for component CSS, and anchor
 * pairing happens when it resolves to "anchor". Call start/stop around
 * open/close — they no-op on the anchor engine. On the floating engine the
 * popover stays hidden from beforetoggle until start() places it. A surface
 * that moves between references (the shared tooltip popup) calls unpairAnchor
 * on the positioning it leaves behind.
 *
 * @param {object} options
 * @param {Element} options.reference - trigger / anchor element
 * @param {Element} options.floating - the [popover] element
 * @param {string} options.namePrefix - unique CSS anchor-name prefix (e.g. "ui-select")
 * @param {string} [options.placement] - Floating UI placement for a surface that
 *   does not hang below its trigger (a tooltip passes its side). Without it the
 *   surface is placed bottom-start, or bottom-end when it has data-align="end".
 * @param {boolean} [options.matchReferenceWidth] - min-width ≥ trigger width (selects)
 * @param {number} [options.minWidth] - floor when matchReferenceWidth (default 128)
 */
export class PopoverPositioning {
  constructor({
    reference,
    floating,
    namePrefix,
    placement,
    matchReferenceWidth = false,
    minWidth = 128
  }) {
    this.reference = reference
    this.floating = floating
    this.namePrefix = namePrefix
    this.placement = placement
    this.engine = resolvePopoverEngine()
    this.matchReferenceWidth = matchReferenceWidth
    this.minWidth = minWidth
    this.cleanup = null
    this.generation = 0
    this.anchorName = null

    if (this.floating) {
      this.floating.dataset.popoverEngine = this.engine
    }
    if (this.engine === "anchor") {
      this.pairAnchor()
    } else {
      this.floating?.addEventListener("beforetoggle", event => this.hideUntilPlaced(event))
    }
  }

  // Callers start on toggle, which can fire after the popover's first frame
  // has painted at the UA default, or at the previous open's stale coords.
  // beforetoggle runs before it shows. A reopen that coalesces with a pending
  // close is still running and placed, so it stays visible.
  hideUntilPlaced(event) {
    if (event.newState === "open" && !this.cleanup) {
      this.floating.style.visibility = "hidden"
    }
  }

  pairAnchor() {
    if (!this.reference || !this.floating) return

    // Explicit pairing: implicit popover-invoker anchors aren't interoperable yet.
    // The name joins the reference's list: a trigger can anchor a dropdown and
    // a tooltip at once. A stale name with this prefix is replaced rather than
    // joined: Turbo restores a snapshot's inline styles, so a controller that
    // pairs on connect would otherwise grow the list on every restoration, and
    // the tooltip leaves its name on a trigger it hides for the exit fade.
    // Other components' names stay.
    const prefix = `--${this.namePrefix}-`
    const otherNames = anchorNames(this.reference).filter(name => !name.startsWith(prefix))
    this.anchorName = `${prefix}${anchorSequence++}`
    this.reference.style.anchorName = [...otherNames, this.anchorName].join(", ")
    this.floating.style.positionAnchor = this.anchorName
  }

  unpairAnchor() {
    if (!this.anchorName) return

    const remaining = anchorNames(this.reference).filter(name => name !== this.anchorName)
    this.reference.style.anchorName = remaining.join(", ")
  }

  start() {
    if (this.engine !== "floating") return
    if (!this.reference || !this.floating || this.cleanup) return

    // Stale styles from the previous open are cleared here rather than on
    // close, so the exit fade holds its position instead of snapping to the
    // UA default (fixed, top-left).
    const clear = { left: "", top: "", inset: "" }
    if (this.matchReferenceWidth) clear.minWidth = ""
    Object.assign(this.floating.style, clear)

    const placement = this.placement ?? this.alignedPlacement

    // Generation token: nulling cleanup alone is not enough — after a rapid
    // reopen cleanup is truthy again under a new autoUpdate, and a late
    // computePosition from the prior open would still write. Bump the token
    // on start and stop so only the current session applies coords.
    this.generation += 1
    const generation = this.generation

    // --available-height mirrors Base UI's variable so component CSS can cap
    // the popover to the space left on its placed side.
    const middleware = [
      offset(4),
      flip(),
      shift({ padding: 4 }),
      size({
        padding: 4,
        apply: ({ availableHeight, rects, elements }) => {
          elements.floating.style.setProperty("--available-height", `${availableHeight}px`)

          if (this.matchReferenceWidth) {
            elements.floating.style.minWidth = `${Math.max(rects.reference.width, this.minWidth)}px`
          }
        }
      })
    ]

    this.cleanup = autoUpdate(this.reference, this.floating, () => {
      computePosition(this.reference, this.floating, {
        placement,
        strategy: "fixed",
        middleware
      }).then(({ x, y }) => {
        if (generation !== this.generation) return
        Object.assign(this.floating.style, { inset: "auto", left: `${x}px`, top: `${y}px`, visibility: "" })
      })
    })
  }

  get alignedPlacement() {
    if (this.floating.dataset.align === "end") return "bottom-end"
    return "bottom-start"
  }

  stop() {
    this.cleanup?.()
    this.cleanup = null
    this.generation += 1
  }
}
